import type {QueryClient} from '@tanstack/react-query'

import type {PaginatedTotalCount} from '../../../../api/memex-items/paginated-views'
import {isQueryPageOfItems} from '../../queries/query-data-helpers'
import {activeMemexItemsQueryFilter} from '../../queries/query-filters'
import {
  getPageTypeFromQueryKey,
  isPageTypeForGroupedItems,
  type PaginatedMemexItemsQueryKey,
} from '../../queries/query-keys'
import type {MemexPageQueryData} from '../../queries/types'
import {
  getQueryDataForMemexItemsTotalCounts,
  incrementMemexItemsTotalCount,
  incrementMemexItemsTotalCountForGroup,
  mergeQueryDataForMemexItemsTotalCounts,
} from '../memex-totals'
import {getActiveMemexItemsQueryVariables} from './item-helpers'
import type {OptimisticUpdateRollbackData} from './update-items'

/**
 * Removes memex items matching ids provided from the client-side state.
 * Triggers a re-render by using a new array object for the updated list of items.
 * If item exists in multiple queries (e.g., in multiple groups), it will be removed from all of them.
 * @param queryClient
 * @param ids List of ids matching items that should be removed.
 */
export function removeMemexItemsFromQueryClient(
  queryClient: QueryClient,
  ids: Array<number>,
): void | OptimisticUpdateRollbackData {
  const variables = getActiveMemexItemsQueryVariables(queryClient)
  const originalTotalCounts = getQueryDataForMemexItemsTotalCounts(queryClient, variables)
  const rollbackData: OptimisticUpdateRollbackData = {queryData: [], totalCounts: originalTotalCounts}
  const idsToRemove = new Set(ids)
  const itemCountRemovedInGroups: {[key: string]: number} = {}

  const activeQueryKeys = queryClient
    .getQueriesData<MemexPageQueryData>(activeMemexItemsQueryFilter)
    .map(([key]) => key) as Array<PaginatedMemexItemsQueryKey>

  for (const queryKey of activeQueryKeys) {
    queryClient.setQueryData<MemexPageQueryData>(queryKey, queryData => {
      if (!isQueryPageOfItems(queryData)) {
        return queryData
      }

      let rollbackDataAddedForQueryKey = false

      const newPageNodes = []
      for (const itemModel of queryData.nodes) {
        if (idsToRemove.has(itemModel.id)) {
          const pageType = getPageTypeFromQueryKey(queryKey)
          if (isPageTypeForGroupedItems(pageType)) {
            itemCountRemovedInGroups[pageType.groupId] = (itemCountRemovedInGroups[pageType.groupId] ?? 0) + 1
            if (pageType.secondaryGroupId) {
              itemCountRemovedInGroups[pageType.secondaryGroupId] =
                (itemCountRemovedInGroups[pageType.secondaryGroupId] ?? 0) + 1
            }
          }
          if (!rollbackDataAddedForQueryKey) {
            rollbackData.queryData.push({queryKey, queryData})
            rollbackDataAddedForQueryKey = true
          }
        } else {
          newPageNodes.push(itemModel)
        }
      }
      return {...queryData, nodes: newPageNodes}
    })
  }

  const newTotalCounts = {
    groups: Object.keys(itemCountRemovedInGroups).reduce(
      (memo, groupId) => {
        const groupItemsRemoved = itemCountRemovedInGroups[groupId] ?? 0
        memo[groupId] = incrementMemexItemsTotalCountForGroup(queryClient, variables, groupId, -groupItemsRemoved)
        return memo
      },
      {} as {[key: string]: PaginatedTotalCount},
    ),
    totalCount: incrementMemexItemsTotalCount(queryClient, variables, -idsToRemove.size),
  }
  mergeQueryDataForMemexItemsTotalCounts(queryClient, variables, newTotalCounts)

  return rollbackData
}
